<template>
  <section class="v2026-ad" :class="'is-w-' + layout">
    <h4 class="v2026-ad-title">แก้ไขเอกสาร AI</h4>

    <div class="v2026-ad-toolbar">
      <button ref="addBtn" type="button" class="v2026-ad-btn-primary" @click="openCreate">
        <span class="v2026-ad-ico v2026-ad-ico--plus" aria-hidden="true"></span>เพิ่มข้อมูล
      </button>
      <button type="button"
              class="v2026-ad-btn-ghost"
              :disabled="isUpdating"
              @click="updateVectorDb">
        {{ isUpdating ? 'กำลังอัพเดต Vector Database...' : 'อัพเดท Vector Database' }}
      </button>
      <LoadingV2026 v-if="isLoading && rows.length" label="กำลังโหลด"/>
    </div>

    <form class="v2026-ad-filters" @submit.prevent="searchFromPageOne">
      <div class="v2026-ad-field">
        <span class="v2026-ad-field__ico" aria-hidden="true"></span>
        <input type="text"
               class="v2026-ad-field__input"
               placeholder="ค้นหาข้อมูล"
               aria-label="ค้นหาเอกสาร AI"
               v-model="searchBox">
        <button v-if="searchBox"
                type="button"
                class="v2026-ad-field__clear"
                aria-label="ล้างคำค้นหา"
                @click="searchBox = ''">×</button>
      </div>
      <select class="v2026-ad-select" aria-label="หมวดหมู่" v-model="selectedCategory">
        <option value="defaultOption">หมวดหมู่ทั้งหมด</option>
        <option v-for="(category, index) in aiDocumentCategories"
                :key="index"
                :value="category">{{ category || 'ยังไม่ระบุหมวดหมู่' }}</option>
      </select>
      <div class="v2026-ad-langs" role="radiogroup" aria-label="ภาษา">
        <label class="v2026-ad-radio">
          <input type="radio" name="v2026-ad-lang" value="th" v-model="selectedLanguage">
          <span>ภาษาไทย</span>
        </label>
        <label class="v2026-ad-radio">
          <input type="radio" name="v2026-ad-lang" value="en" v-model="selectedLanguage">
          <span>ภาษาอังกฤษ</span>
        </label>
      </div>
      <button type="submit" class="v2026-ad-btn-ghost">ค้นหา</button>
      <button type="button" class="v2026-ad-btn-ghost" @click="clearSearch">ล้างข้อมูล</button>
    </form>

    <div ref="adList"
         class="v2026-ad-list"
         :class="['is-w-' + layout, { 'is-empty': !hasRows }]"
         :aria-busy="isLoading ? 'true' : 'false'">
      <div v-if="hasRows" class="v2026-ad-head" aria-hidden="true">
        <span>ID</span>
        <span>เนื้อหา</span>
        <span v-if="layout !== 'sm'">ภาษา</span>
        <span v-if="layout !== 'sm'">หมวดหมู่</span>
        <span v-if="layout === 'lg'">สร้างเมื่อ</span>
        <span v-if="layout === 'lg'">แก้ไขล่าสุด</span>
        <span></span>
      </div>

      <div v-for="document in rows" :key="document.id" class="v2026-ad-row">
        <div class="v2026-ad-id">
          <span class="v2026-ad-sr">ID: </span>{{ document.id }}
        </div>
        <div class="v2026-ad-body" :title="document.detail">
          <span class="v2026-ad-sr">เนื้อหา: </span>{{ document.detail }}
          <div v-if="layout === 'sm'" class="v2026-ad-caption">
            {{ languageLabel(document.language) }} · {{ categoryLabel(document.category) }}
          </div>
        </div>
        <div v-if="layout !== 'sm'" class="v2026-ad-lang">
          <span class="v2026-ad-sr">ภาษา: </span>{{ languageLabel(document.language) }}
        </div>
        <div v-if="layout !== 'sm'" class="v2026-ad-cat" :title="categoryLabel(document.category)">
          <span class="v2026-ad-sr">หมวดหมู่: </span>{{ categoryLabel(document.category) }}
        </div>
        <div v-if="layout === 'lg'" class="v2026-ad-date">
          <span class="v2026-ad-sr">สร้างเมื่อ: </span>{{ pretty(document.created_at) }}
        </div>
        <div v-if="layout === 'lg'" class="v2026-ad-date">
          <span class="v2026-ad-sr">แก้ไขล่าสุด: </span>{{ pretty(document.updated_at) }}
        </div>
        <div class="v2026-ad-actions">
          <button type="button"
                  class="v2026-ad-btn-row"
                  :aria-label="'แก้ไขเอกสาร ' + document.id"
                  @click="openEdit(document)">
            <span class="v2026-ad-ico v2026-ad-ico--edit" aria-hidden="true"></span>แก้ไข
          </button>
          <button type="button"
                  class="v2026-ad-btn-row v2026-ad-btn-row--danger"
                  :aria-label="'ลบเอกสาร ' + document.id"
                  @click="openDelete(document)">
            <span class="v2026-ad-ico v2026-ad-ico--trash" aria-hidden="true"></span>ลบ
          </button>
        </div>
      </div>
    </div>

    <div v-if="isLoading && !rows.length" class="v2026-ad-loading" role="status">
      <LoadingV2026 label="กำลังโหลด"/>
    </div>

    <div v-else-if="loadError && !rows.length" class="v2026-ad-empty" role="status">
      <span class="v2026-ad-empty__ico" aria-hidden="true"></span>
      <div class="v2026-ad-empty__title">โหลดเอกสารไม่สำเร็จ</div>
      <div class="v2026-ad-empty__msg">{{ loadError }}</div>
      <button type="button" class="v2026-ad-btn-ghost v2026-ad-empty__act" @click="fetchAiDocuments">ลองใหม่</button>
    </div>

    <div v-else-if="emptyState === 'no-result'" class="v2026-ad-empty" role="status">
      <span class="v2026-ad-empty__ico v2026-ad-empty__ico--search" aria-hidden="true"></span>
      <div class="v2026-ad-empty__title">ไม่พบข้อมูล</div>
      <div class="v2026-ad-empty__msg">ลองใช้คำอื่น หรือล้างตัวกรองเพื่อดูเอกสารทั้งหมด</div>
      <button type="button" class="v2026-ad-btn-ghost v2026-ad-empty__act" @click="clearSearch">ล้างข้อมูล</button>
    </div>

    <div v-else-if="emptyState === 'no-data'" class="v2026-ad-empty" role="status">
      <span class="v2026-ad-empty__ico" aria-hidden="true"></span>
      <div class="v2026-ad-empty__title">ยังไม่มีเอกสาร AI</div>
      <div class="v2026-ad-empty__msg">เพิ่มเอกสารเพื่อให้ผู้ช่วย AI ใช้อ้างอิง</div>
      <button type="button" class="v2026-ad-btn-ghost v2026-ad-empty__act" @click="openCreate">เพิ่มข้อมูล</button>
    </div>

    <div v-if="hasRows" class="v2026-ad-pager">
      <span class="v2026-ad-pager__count">แสดงหน้า {{ queryParams.page }} จาก {{ pageCount }}</span>
      <nav class="v2026-ad-pages" aria-label="เปลี่ยนหน้า">
        <button type="button"
                class="v2026-ad-page v2026-ad-page--prev"
                aria-label="หน้าก่อนหน้า"
                :disabled="queryParams.page <= 1"
                @click="clickPaginate(queryParams.page - 1)">
          <span class="v2026-ad-page__ico" aria-hidden="true"></span>
        </button>
        <template v-for="(p, i) in pageItems">
          <span v-if="p === '…'" :key="'e' + i" class="v2026-ad-ellipsis" aria-hidden="true">…</span>
          <button v-else
                  :key="p"
                  type="button"
                  class="v2026-ad-page"
                  :class="{ 'is-current': p === queryParams.page }"
                  :aria-current="p === queryParams.page ? 'page' : null"
                  :aria-label="'หน้า ' + p"
                  @click="clickPaginate(p)">{{ p }}</button>
        </template>
        <button type="button"
                class="v2026-ad-page v2026-ad-page--next"
                aria-label="หน้าถัดไป"
                :disabled="queryParams.page >= pageCount"
                @click="clickPaginate(queryParams.page + 1)">
          <span class="v2026-ad-page__ico" aria-hidden="true"></span>
        </button>
      </nav>
    </div>

    <AiDocumentFormModalV2026 :open="modal.open"
                              :mode="modal.mode"
                              :document="modal.document"
                              :categories="aiDocumentCategories"
                              :saving="isSaving"
                              @submit="submitDocument"
                              @close="closeModal"/>

    <BaseConfirm :open="confirm.open"
                 :title="'ลบเอกสาร ' + (confirm.id || '') + ' ?'"
                 message="เอกสารนี้จะถูกลบถาวรและกู้คืนไม่ได้"
                 confirmLabel="ลบ"
                 cancelLabel="ยกเลิก"
                 variant="danger"
                 :busy="confirm.busy"
                 @confirm="acceptDelete"
                 @cancel="cancelDelete"/>

    <BaseToast :open="toast.open"
               :variant="toast.variant"
               :title="toast.title"
               :message="toast.message"
               :autoHideDelay="toast.variant === 'danger' ? 5000 : 2500"
               @close="toast.open = false"/>
  </section>
</template>

<script>
import axios from 'axios'
import AiDocumentFormModalV2026 from '../components/modal/AiDocumentFormModalV2026'
import BaseConfirm from '../components/coversation/BaseConfirm'
import BaseToast from '../components/coversation/BaseToast'
import LoadingV2026 from '../components/coversation/LoadingV2026'

export default {
  components: {
    AiDocumentFormModalV2026,
    BaseConfirm,
    BaseToast,
    LoadingV2026
  },
  data () {
    return {
      aiDocumentCategories: [],
      selectedCategory: 'defaultOption',
      aiDocuments: [],
      URL: '/ai-documents',
      searchBox: '',
      queryParams: {
        page: 1,
        limit: 10
      },
      pageCount: 1,
      selectedLanguage: 'th',
      isUpdating: false,
      isLoading: false,
      isSaving: false,
      loadError: '',
      emptyState: null,
      layout: 'lg',
      modal: {
        open: false,
        mode: 'create',
        document: null
      },
      confirm: {
        open: false,
        id: null,
        busy: false
      },
      toast: {
        open: false,
        variant: 'success',
        title: '',
        message: ''
      }
    }
  },
  computed: {
    rows () {
      return this.emptyState ? [] : (this.aiDocuments || [])
    },
    hasRows () {
      return this.rows.length > 0
    },
    hasActiveFilter () {
      return !!(this.searchBox || this.selectedCategory !== 'defaultOption' || this.selectedLanguage !== 'th')
    },
    pageItems () {
      const total = this.pageCount || 1
      const cur = this.queryParams.page
      if (total <= 7) {
        const a = []
        for (let i = 1; i <= total; i++) a.push(i)
        return a
      }
      let start = Math.max(2, cur - 2)
      let end = Math.min(total - 1, cur + 2)
      if (cur <= 4) {
        start = 2
        end = 5
      }
      if (cur >= total - 3) {
        start = total - 4
        end = total - 1
      }
      const out = [1]
      if (start > 2) out.push('…')
      for (let i = start; i <= end; i++) out.push(i)
      if (end < total - 1) out.push('…')
      out.push(total)
      return out
    }
  },
  watch: {
    searchBox (newVal, oldVal) {
      if (!newVal && oldVal) this.searchFromPageOne()
    }
  },
  methods: {
    categoryLabel (category) {
      return category || 'ยังไม่ระบุหมวดหมู่'
    },
    languageLabel (language) {
      if (language === 'th') return 'ไทย'
      if (language === 'en') return 'English'
      return language || '—'
    },
    pretty (value) {
      const fn = this.$options.filters && this.$options.filters.prettyDate
      return (fn && fn(value)) || '—'
    },
    errorText (err) {
      const body = err && err.response && err.response.data
      return (body && body.error) || (err && err.message) || 'เกิดข้อผิดพลาด'
    },
    showToast (variant, title, message) {
      this.toast = { open: true, variant, title, message: message || '' }
    },
    searchFromPageOne () {
      this.queryParams.page = 1
      return this.fetchAiDocuments()
    },
    clearSearch () {
      this.searchBox = ''
      this.selectedLanguage = 'th'
      this.selectedCategory = 'defaultOption'
      this.searchFromPageOne()
    },
    clickPaginate (page) {
      if (page < 1 || page > this.pageCount) return
      this.queryParams.page = page
      this.fetchAiDocuments()
    },
    async fetchAiDocumentCategories () {
      try {
        const { data } = await axios.get(this.URL + '/categories')
        this.aiDocumentCategories = data || []
      } catch (err) {
        this.showToast('danger', 'โหลดหมวดหมู่ไม่สำเร็จ', this.errorText(err))
      }
    },
    async fetchAiDocuments () {
      this.isLoading = true
      this.loadError = ''
      try {
        const { data } = await axios.get(this.URL + '/index', {
          params: {
            document_category: this.selectedCategory,
            keyword: this.searchBox || null,
            page: this.queryParams.page,
            limit: this.queryParams.limit,
            language: this.selectedLanguage
          }
        })
        this.aiDocuments = (data && data.documents) || []
        this.pageCount = (data && data.pageCount) || 1
        if (this.aiDocuments.length === 0) {
          this.queryParams.page = 1
          this.emptyState = this.hasActiveFilter ? 'no-result' : 'no-data'
        } else {
          this.emptyState = null
        }
      } catch (err) {
        this.aiDocuments = []
        this.loadError = this.errorText(err)
        this.emptyState = null
        this.showToast('danger', 'โหลดเอกสารไม่สำเร็จ', this.loadError)
      } finally {
        this.isLoading = false
        this.$nextTick(this.measureLayout)
      }
    },
    async refresh () {
      await this.fetchAiDocuments()
      await this.fetchAiDocumentCategories()
    },
    async updateVectorDb () {
      this.isUpdating = true
      try {
        await axios.get('/ai-assistant/generate-faiss-db')
        this.showToast('success', 'อัพเดท Vector Database สำเร็จ', '')
      } catch (err) {
        this.showToast('danger', 'อัพเดท Vector Database ไม่สำเร็จ', this.errorText(err))
      } finally {
        this.isUpdating = false
      }
    },
    openCreate () {
      this.modal = { open: true, mode: 'create', document: null }
    },
    openEdit (document) {
      this.modal = { open: true, mode: 'edit', document: document }
    },
    closeModal () {
      this.modal = { open: false, mode: 'create', document: null }
    },
    async submitDocument (payload) {
      this.isSaving = true
      try {
        if (this.modal.mode === 'edit' && this.modal.document) {
          const document = this.modal.document
          await axios.put(this.URL + '/' + document.id, {
            selectedDocumentId: document.id,
            formData: payload.message,
            created_at: document.created_at,
            updated_at: document.updated_at,
            category: payload.category,
            language: payload.language
          })
          this.showToast('success', 'อัพเดทข้อมูลสำเร็จ', '')
        } else {
          await axios.post(this.URL, {
            category: payload.category,
            message: payload.message,
            language: payload.language
          })
          this.showToast('success', 'เพิ่มข้อมูลสำเร็จ', '')
        }
        this.closeModal()
        await this.refresh()
      } catch (err) {
        this.showToast('danger', this.modal.mode === 'edit' ? 'อัพเดทข้อมูลล้มเหลว' : 'เพิ่มข้อมูลไม่สำเร็จ', this.errorText(err))
      } finally {
        this.isSaving = false
      }
    },
    openDelete (document) {
      this.confirm = { open: true, id: document.id, busy: false }
    },
    cancelDelete () {
      this.confirm = { open: false, id: null, busy: false }
    },
    async acceptDelete () {
      const id = this.confirm.id
      if (!id) return
      this.confirm.busy = true
      try {
        await axios.delete(this.URL + '/' + id)
        await this.fetchAiDocumentCategories()
        if (!this.aiDocumentCategories.includes(this.selectedCategory)) {
          this.selectedCategory = 'defaultOption'
        }
        await this.fetchAiDocuments()
        this.showToast('success', 'ลบเอกสารสำเร็จ', '')
        this.cancelDelete()
      } catch (err) {
        this.confirm.busy = false
        this.showToast('danger', 'ลบไม่สำเร็จ', this.errorText(err))
      }
    },
    measureLayout () {
      const el = this.$refs.adList
      if (!el) return
      const w = el.clientWidth
      this.layout = w >= 1040 ? 'lg' : (w >= 720 ? 'md' : 'sm')
    }
  },
  mounted () {
    this.refresh()
    this.$nextTick(this.measureLayout)
    if (typeof ResizeObserver !== 'undefined') {
      this.$nextTick(() => {
        if (!this.$refs.adList) return
        this._ro = new ResizeObserver(this.measureLayout)
        this._ro.observe(this.$refs.adList)
      })
    }
    window.addEventListener('resize', this.measureLayout)
  },
  beforeDestroy () {
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
    }
    window.removeEventListener('resize', this.measureLayout)
  }
}
</script>

<style lang="scss" scoped>
/* Only the ten --v2026-color-* tokens declared on .v2026-shell resolve under /settings, so every
   grey below is hard-coded on purpose. */
.v2026-ad {
  padding: 16px 16px 48px;
  background: #ffffff;
}

.v2026-ad-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
}

.v2026-ad-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 12px 0 8px;
}

.v2026-ad-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 0 12px;
}

.v2026-ad-field {
  position: relative;
  width: 260px;
  max-width: 100%;

  .is-w-sm & {
    width: 100%;
    flex: 1 1 100%;
  }
}

.v2026-ad-field__input {
  width: 100%;
  height: 36px;
  padding: 0 34px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;

  &::placeholder { color: #66696C; }
  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-ad-field__ico {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 16px;
  height: 16px;
  pointer-events: none;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask: url('~@/assets/icons2026/search.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/search.svg') center / contain no-repeat;
}

.v2026-ad-field__clear {
  position: absolute;
  right: 6px;
  top: 6px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: none;
  font-size: 16px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;

  &:hover { background: var(--v2026-color-hover-bg); color: #212529; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-ad-select {
  height: 36px;
  min-width: 180px;
  max-width: 100%;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;

  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-ad-langs {
  display: flex;
  align-items: center;
  gap: 16px;
}

.v2026-ad-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  color: #212529;
  cursor: pointer;

  input { margin: 0; }
}

.v2026-ad-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--v2026-color-main);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(.94); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-ad-btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-ad-list {
  --ad-cols: 64px minmax(0, 1.6fr) 88px 140px 130px 130px 156px;
  border-top: 1px solid #e9ecef;

  &.is-w-md { --ad-cols: 64px minmax(0, 1fr) 88px 140px 156px; }
  &.is-w-sm { --ad-cols: 56px minmax(0, 1fr) auto; }
  &.is-empty { border-top: 0; }
}

.v2026-ad-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: var(--ad-cols);
  column-gap: 12px;
  align-items: center;
  height: 40px;
  padding: 0 8px;
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  font-size: 12px;
  font-weight: 600;
  color: #66696C;

  .is-w-sm & { display: none; }
}

.v2026-ad-row {
  display: grid;
  grid-template-columns: var(--ad-cols);
  column-gap: 12px;
  align-items: start;
  padding: 12px 8px;
  border-bottom: 1px solid #e9ecef;

  &:last-child { border-bottom: 0; }

  .is-w-sm & {
    grid-template-areas: "id body act";
    align-items: center;
  }
}

.v2026-ad-id {
  font-size: 13px;
  color: #66696C;
  padding-top: 2px;

  .is-w-sm & { grid-area: id; }
}

.v2026-ad-body {
  min-width: 0;
  font-size: 14px;
  color: #212529;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  .is-w-sm & { grid-area: body; }
}

.v2026-ad-caption {
  margin-top: 4px;
  font-size: 12px;
  color: #66696C;
}

.v2026-ad-lang,
.v2026-ad-cat,
.v2026-ad-date {
  min-width: 0;
  font-size: 13px;
  color: #66696C;
  word-break: break-word;
}

.v2026-ad-cat {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.v2026-ad-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;

  .is-w-sm & { grid-area: act; }
}

.v2026-ad-btn-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 13px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-ad-btn-row--danger {
  color: #E5484D;

  &:hover:not(:disabled) { background: #FEF2F2; border-color: rgba(229, 72, 77, .40); }
}

.v2026-ad-ico {
  display: inline-block;
  width: 16px;
  height: 16px;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

.v2026-ad-ico--plus {
  background-color: #ffffff;
  -webkit-mask-image: url('~@/assets/icons2026/plus.svg');
          mask-image: url('~@/assets/icons2026/plus.svg');
}

.v2026-ad-ico--edit {
  -webkit-mask-image: url('~@/assets/icons2026/pencil-edit.svg');
          mask-image: url('~@/assets/icons2026/pencil-edit.svg');
}

.v2026-ad-ico--trash {
  background-color: #E5484D;
  -webkit-mask-image: url('~@/assets/icons2026/trash.svg');
          mask-image: url('~@/assets/icons2026/trash.svg');
}

.v2026-ad-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 224px;
}

.v2026-ad-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 56px 16px;
}

.v2026-ad-empty__ico {
  width: 40px;
  height: 40px;
  background-color: #66696C;
  opacity: .55;
  -webkit-mask: url('~@/assets/icons2026/ai-magic.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/ai-magic.svg') center / contain no-repeat;
}

.v2026-ad-empty__ico--search {
  -webkit-mask-image: url('~@/assets/icons2026/search.svg');
          mask-image: url('~@/assets/icons2026/search.svg');
}

.v2026-ad-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-ad-empty__msg {
  font-size: 13px;
  color: #66696C;
  max-width: 420px;
}

.v2026-ad-empty__act { margin-top: 8px; }

.v2026-ad-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.v2026-ad-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.v2026-ad-pager__count {
  font-size: 13px;
  color: #66696C;
}

.v2026-ad-pages {
  display: flex;
  align-items: center;
  gap: 4px;
}

.v2026-ad-page {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 13px;
  color: #343a40;
  cursor: pointer;

  &:hover:not(:disabled):not(.is-current) { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .4; cursor: default; }

  &.is-current {
    background: var(--v2026-color-main-soft);
    border-color: var(--v2026-color-main);
    color: var(--v2026-color-main-icon);
    font-weight: 600;
  }
}

.v2026-ad-page--prev,
.v2026-ad-page--next {
  padding: 0;
  width: 32px;
}

.v2026-ad-page__ico {
  display: inline-block;
  width: 16px;
  height: 16px;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

.v2026-ad-page--prev .v2026-ad-page__ico {
  -webkit-mask-image: url('~@/assets/icons2026/chevron-left.svg');
          mask-image: url('~@/assets/icons2026/chevron-left.svg');
}

.v2026-ad-page--next .v2026-ad-page__ico {
  -webkit-mask-image: url('~@/assets/icons2026/chevron-right.svg');
          mask-image: url('~@/assets/icons2026/chevron-right.svg');
}

.v2026-ad-ellipsis {
  min-width: 24px;
  text-align: center;
  color: #66696C;
}
</style>
